import {
  DataGridPro as MuiDataGrid,
  DataGridProProps,
  GridRowModel,
  deDE,
  enUS
} from '@mui/x-data-grid-pro';

import { Checkbox } from '../../atomic';
import { TableDataGridPagination } from './pagination';
import { TableDataGridActionBar } from './actionbar';
import { SortAscendingIcon, SortDescendingIcon } from './sortIcons';
import { TableRoofProps } from '../tables/presentation/table/roofs';
import { DotsMenuIcon } from '../../../icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LinearProgress, Popper } from '@mui/material';
import { EmptyTableOverlay } from './empty';

export type ThreepointMenu = {
  threepointMenu?: boolean;
  onRowThreepointClicked?: (row: GridRowModel) => void;
};

export type TableDataGridProps = ThreepointMenu &
  Omit<DataGridProProps, 'disableColumnMenu'> &
  Pick<
    TableRoofProps,
    | 'threePointMenuConfig'
    | 'actionButtonConfig'
    | 'additionalComponent'
    | 'customTableRoofHeadline'
  > & {
    hideToolbar?: boolean;
  };

//const ROW_HEIGHT_SMALL = 48;
//const ROW_HEIGHT_LARGE = 56;
const FONT_SIZE_SMALL = 14;
const FONT_SIZE_LARGE = 16;

/*
  const ROW_HEIGHT_CSS = {
    xs: `${ROW_HEIGHT_SMALL}px !important`,
    md: `${ROW_HEIGHT_LARGE}px !important`
  };
  */

const FONT_SIZE_CSS = {
  xs: `${FONT_SIZE_SMALL}px`,
  md: `${FONT_SIZE_LARGE}px`
};

const ActionIcon: FC = () => {
  return <DotsMenuIcon className="dotsMenu" color="grey.500" />;
};

export const TableDataGrid: React.FC<TableDataGridProps> = ({
  rows,
  columns,
  checkboxSelection = true,
  disableSelectionOnClick = true,
  threePointMenuConfig,
  actionButtonConfig,
  hideToolbar = false,
  additionalComponent,
  customTableRoofHeadline,
  sx,
  ...props
}) => {
  const { i18n } = useTranslation();
  const locales = {
    en: enUS.components.MuiDataGrid.defaultProps.localeText,
    de: deDE.components.MuiDataGrid.defaultProps.localeText
  };

  return (
    <MuiDataGrid
      rows={rows}
      columns={columns}
      localeText={locales[i18n.language as keyof typeof locales]}
      rowHeight={56}
      sx={{
        minHeight: '400px',
        border: 0,
        '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
          boxShadow: 'unset',
          '&:focus-within': { outline: 'none' }
        },
        '& .MuiDataGrid-row.MuiDataGrid-row--editing': {
          boxShadow: 'unset',
          '&:focus-within': { outline: 'none' }
        },
        '& .MuiDataGrid-checkboxInput': {
          paddingLeft: '28px'
        },
        '& .MuiDataGrid-virtualScroller': {
          marginTop: '48px !important',

          paddingBottom: '12px !important'
        },
        '& .MuiDataGrid-columnHeaders': {
          color: 'grey.600',
          fontSize: FONT_SIZE_CSS,
          border: 0,

          '& .MuiDataGrid-columnHeader': {
            outline: 'none',
            padding: '0 8px',

            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: '500',
              fontSize: '12px',
              lineHeight: '14px'
            },

            '& .MuiDataGrid-columnHeaderTitleContainer': {
              height: 40,
              marginBottom: '16px !important'
            },

            '& .MuiDataGrid-columnHeaderTitleContainerContent > span.MuiCheckbox-root': {
              padding: '0 !important'
            },

            '& .MuiDataGrid-checkboxInput': {
              paddingLeft: '12px !important'
            },

            '& .MuiIconButton-root': {
              background: 'none',
              padding: 0,

              '& .MuiDataGrid-sortIcon': {
                width: '24px',
                height: '24px'
              }
            },

            '&.MuiDataGrid-columnHeader--sorted': {
              color: 'primary.main'
            },
            '&:hover': {
              color: 'primary.main'
            }
          },

          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          }
        },

        '& .MuiDataGrid-row': {
          borderColor: 'white',
          borderTopColor: 'grey.50',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderBottomWidth: 0,

          '&:hover': {
            backgroundColor: 'grey.50',
            borderColor: 'white',
            '& + div': {
              borderColor: 'white !important'
            },
            borderRadius: 2,

            '& +.MuiDataGrid-row': {
              borderTopColor: 'transparent'
            },

            '& .dotsMenu': {
              display: 'block'
            }
          },
          '&.Mui-selected': {
            backgroundColor: 'grey.50',
            borderRadius: 2,
            borderColor: 'white',
            '& + div': {
              borderColor: 'white'
            },

            '& .dotsMenu': {
              display: 'block'
            },

            '&:hover': {
              backgroundColor: 'grey.50',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'grey.100',
              '& + div': {
                borderTop: 'none'
              }
            }
          },
          '&:last-child': {
            borderBottomWidth: '1px'
          },
          '& .MuiDataGrid-cell': {
            color: 'primary.main',
            fontSize: FONT_SIZE_CSS,
            paddingX: '16px',
            border: 0,
            outline: 'none',
            '&:last-child': {
              marginLeft: '-3px'
            }
          },
          '& .dotsMenu': {
            display: 'none'
          }
        },
        '& .MuiDataGrid-footerContainer': {
          borderTopColor: 'grey.50'
        },

        '& .newRow': {
          backgroundColor: '#EAEEFF',
          borderColor: 'white',
          '&:hover': {
            backgroundColor: '#EAEEFF'
          }
        },

        '& .oldRow': {
          backgroundColor: '#FFE9E8',
          borderColor: 'white',
          '&:hover': {
            backgroundColor: '#FFE9E8'
          }
        },
        ...sx
      }}
      componentsProps={{
        toolbar: {
          actionButtonConfig,
          threePointMenuConfig,
          additionalComponent,
          customTableRoofHeadline
        },
        noRowsOverlay: {
          variant: 'rows'
        },
        noResultsOverlay: {
          variant: 'results'
        },
        ...props.componentsProps
      }}
      checkboxSelection={checkboxSelection}
      disableSelectionOnClick={disableSelectionOnClick}
      disableColumnMenu
      hideFooterSelectedRowCount
      {...props}
      components={{
        BaseCheckbox: Checkbox,
        Pagination: rows.length === 0 ? null : TableDataGridPagination,
        Toolbar: hideToolbar ? undefined : TableDataGridActionBar,
        ColumnSortedAscendingIcon: SortAscendingIcon,
        ColumnSortedDescendingIcon: SortDescendingIcon,
        MoreActionsIcon: ActionIcon,
        BasePopper: (e) => {
          return (
            <Popper {...e}>
              <Popper {...e} className="shadow-500 rounded-xl" />
            </Popper>
          );
        },
        NoRowsOverlay: EmptyTableOverlay,
        NoResultsOverlay: EmptyTableOverlay,
        LoadingOverlay: () => (
          <LinearProgress color="neutral" sx={{ zIndex: 100, marginTop: -0.5 }} />
        ),
        ...props.components
      }}
    />
  );
};
